import { i18n } from '@/plugins/vue-i18n';

/**
 * Convert number to word.
 * e.g. 3 => 'month'
 *
 * @param unit
 * @param factor (optional)
 * @param dative (optional) boolean
 * @returns {string}
 */
export const unitToString = (unit, factor, dative) => {
  if (!unit) unit = '';
  unit = unit.toString();

  if (!factor) factor = 1;
  factor = factor.toString();

  const suffix = (factor > 1 ? 's' : '') + (dative ? '_dative' : '');

  switch (unit) {
    case '1':
      return `day${suffix}`;
    case '2':
      return `week${suffix}`;
    case '3':
      return `month${suffix}`;
    case '5':
      return `hour${suffix}`;
    case '6':
      return `minute${suffix}`;
    case '12':
      return `year${suffix}`;
    default:
      return 'unit_unknown';
  }
};

/**
 * e.g. "pro Monat" / "pro 3 Wochen"
 * @param unit
 * @param factor
 * @returns {string}
 */
export const chargingInterval = (unit, factor) => {
  if (!factor) factor = 1;

  const parsedUnit = unitToString(unit, factor);

  return i18n.tc('common.charging_interval', factor, {
    unit: i18n.t(`common.${parsedUnit}`),
    count: factor,
  });
};

/**
 * e.g. "nach 1 Monat" / "nach 3 Wochen"
 * @param unit
 * @param factor
 * @returns {string}
 */
export const chargingAfter = (unit, factor) => {
  if (!factor) factor = 1;

  const parsedUnit = unitToString(unit, factor, true);

  return i18n.tc('common.charging_after', factor, {
    unit: i18n.t(`common.${parsedUnit}`),
    count: factor,
  });
};

/**
 * e.g. "Im 1. Monat"
 * @param unit
 * @param suffix
 * @returns {VueI18n.TranslateResult}
 */
export const fromFirstUnit = (unit, suffix) => {
  if (suffix) suffix = `_${suffix}`;
  else suffix = '';

  return i18n.t(`common.from_first_${unitToString(unit)}${suffix}`);
};

/**
 * Convert XTC supplied cycle strings into human readable strings.
 * Note: Strings with multiple units (e.g.: "1M 3w") are not supported.
 * @param input xtc cycle string e.g.:“1M”
 * @returns human readable string e.g.: "per month"
 */
export const convertChargingCycle = input => convertXTC(chargingInterval, input);

/**
 * Convert XTC supplied cycle strings into human readable strings.
 * Note: Strings with multiple units (e.g.: "1M 3w") are not supported.
 * @param input xtc cycle string e.g.:“1M”
 * @returns human readable string e.g.: "after 1 month"
 */
export const convertChargingCycleAfter = input => convertXTC(chargingAfter, input);

export const convertXTC = (convertFn, input) => {
  if (!input) return null;
  const match = input.match(/^\s*(\d+)\s*([mhdwMy])\s*$/);
  // fail silently
  if (!match || match[0] !== input) return input;
  const [, factor, unit] = match;

  const transformUnit = input => {
    switch (input) {
      case 'm':
        return 6;
      case 'h':
        return 5;
      case 'd':
        return 1;
      case 'w':
        return 2;
      case 'M':
        return 3;
      case 'y':
        return 12;
      default:
        return null;
    }
  };

  return convertFn(transformUnit(unit), Number(factor));
};

export default {
  unitToString,
  chargingInterval,
  chargingAfter,
  fromFirstUnit,
  convertChargingCycle,
  convertChargingCycleAfter,
  convertXTC,
};
